import React, { useState } from "react";

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
  getPaginationRowModel,
  Row,
} from "@tanstack/react-table";
import { useAppSelector } from "store";
import { Session } from "store/slices/submissions";
import SubmissionsModal from "components/modals/SubmissionsModal/SubmissionsModal";
import { submissionStatus } from "constants/sybmissions";
import DropDownElement from "../DropDownElement/DropDownElement";
import { periods } from "../../constants";

const columnHelper = createColumnHelper<Session>();

const columns = [
  columnHelper.accessor("submissions", {
    cell: (info) => {
      const data = info.getValue()[0];
      return (
        <>
          {data?.url && (
            <img
              src={data.url}
              alt="Session"
              width={"54"}
              style={{ borderRadius: 12 }}
            />
          )}
        </>
      );
    },
    header: () => "Photo",
    size: 80,
  }),
  columnHelper.accessor("contact.firstName", {
    cell: (info) => info.getValue(),
    header: () => "First Name",
    size: 100,
  }),
  columnHelper.accessor("contact.lastName", {
    cell: (info) => info.getValue(),
    header: () => "Last Name",
    size: 100,
  }),
  columnHelper.accessor("contact.email", {
    cell: (info) => info.getValue(),
    header: () => "Email",
    size: 140,
  }),
  columnHelper.accessor("studio.name", {
    header: () => "Studio",
    cell: (info) => info.renderValue(),
    size: 100,
  }),
  columnHelper.accessor("sessionCreatedAt", {
    header: () => "Created at",
    cell: (info) => info.renderValue(),
    size: 80,
  }),
];
const AllSubmissionsDataGrid = ({
  fetchSubmissionsData,
  dataType = "all",
  setSelectedStudio,
  selectedStudio,
  selectedPeriod,
  setSelectedPeriod,
  setPageIndex,
  pageIndex,
  setPageSize,
  totalPages,
}: {
  fetchSubmissionsData: any;
  dataType: string;
  selectedStudio: { id: number; name: string } | null;
  setSelectedStudio: (val: { id: number; name: string }) => void;
  selectedPeriod: { id: number; name: string } | null;
  setSelectedPeriod: (val: { id: number; name: string }) => void;
  setPageIndex: (val: number) => void;
  pageIndex: number;
  setPageSize: (val: number) => void;
  totalPages: number;
}) => {
  const { allSubmissions, reviewData, rejectedData, acceptedData } =
    useAppSelector((state) => state.submissions);

  const [isActionsModalOpen, toggleActionsModal] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [activeSubmission, setActiveSubmission] = useState<Session | null>(
    null,
  );

  const { studioNames } = useAppSelector((state) => state.studios);

  const table = useReactTable({
    data:
      dataType === "all"
        ? allSubmissions
        : dataType === submissionStatus.review.name
          ? reviewData
          : dataType === submissionStatus.rejected.name
            ? rejectedData
            : dataType === submissionStatus.accepted.name
              ? acceptedData
              : [],
    columns,
    state: {
      sorting,
    },
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const onCloseActionsModal = () => toggleActionsModal(false);

  const onOpenContactActions = (row: Row<Session>) => {
    setActiveSubmission(row.original);
    toggleActionsModal(true);
  };

  const handleNextPage = () => {
    if (totalPages !== pageIndex + 1) {
      setPageIndex(pageIndex + 1);
    }
  };

  const handlePrevPage = () => {
    if (pageIndex !== 0) {
      setPageIndex(pageIndex - 1);
    }
  };

  console.log("ttt", totalPages, pageIndex);

  return (
    <>
      <div
        className="padding-h24 mt-20 flex flex-end a-items-c"
        style={{ height: 30 }}
      >
        <div className="flex gap8 a-items-c">
          <p className={"gold-text font11"}>Studio</p>
          <div>
            <DropDownElement
              selectedStudio={selectedStudio}
              setSelectedStudio={setSelectedStudio}
              title="All studios"
              data={studioNames}
            />
          </div>
          <div>
            <DropDownElement
              selectedStudio={selectedPeriod}
              setSelectedStudio={setSelectedPeriod}
              title="Submitted after"
              data={periods}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-col pt-4 pb-6 full-width padding24">
        <table className={"full-width"} style={{ tableLayout: "fixed" }}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="table-border-b table-header">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="py-4 font-medium t-align-left"
                    style={{
                      width: header.getSize(),
                    }}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        {...{
                          className: header.column.getCanSort()
                            ? "cursor-pointer select-none flex min-w-[36px] flex-start position-relative"
                            : "",
                          onClick: header.column.getToggleSortingHandler(),
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {{
                          asc: <span className="pl-2">↑</span>,
                          desc: <span className="pl-2">↓</span>,
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          {table.getRowModel()?.rows?.length > 0 ? (
            <tbody style={{ width: "100%" }}>
              {table.getRowModel()?.rows?.map((row) => (
                <tr
                  key={row.id}
                  className="table-border-b custom-table-row"
                  onClick={() => onOpenContactActions(row)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="pt-[14px] pb-[18px] table-item"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          ) : (
            <tbody>
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center py-4"
                  style={{ height: 280 }}
                >
                  <p className="gray5-text font-s20">No rows</p>
                </td>
              </tr>
            </tbody>
          )}
        </table>

        <div
          className="sm:flex-row flex-col w-full mt-8 items-center text-xs gap24 justify-end"
          style={{ display: "flex" }}
        >
          {table.getRowModel()?.rows?.length > 0 && (
            <div className="sm:mb-0 mb-2">
              <span className="mr-2 text-white">Rows per page:</span>
              <select
                className="p-1 w-16 table-selector-bg"
                value={table.getState().pagination.pageSize}
                onChange={(e) => {
                  table.setPageSize(Number(e.target.value));
                  setPageSize(Number(e.target.value));
                  setPageIndex(0);
                }}
              >
                {[10, 20, 50, 100].map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    {pageSize}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="flex gap-2">
            <button
              className={`${
                pageIndex === 0
                  ? "bg-gray-100 next-btn-disable"
                  : "hover:bg-gray-200 hover:curstor-pointer bg-gray-100 next-btn-active"
              } rounded p-1`}
              onClick={() => handlePrevPage()}
              disabled={pageIndex === 0}
            >
              <span className="w-5 h-5 text-white">{"<"}</span>
            </button>
            <button
              className={`${
                totalPages === pageIndex + 1
                  ? "bg-gray-100 next-btn-disable"
                  : "hover:bg-gray-200 hover:curstor-pointer bg-gray-100 next-btn-active"
              } rounded p-1`}
              onClick={() => handleNextPage()}
              disabled={totalPages === pageIndex + 1}
            >
              <span className="w-5 h-5 text-white">{">"}</span>
            </button>
          </div>
        </div>
        <SubmissionsModal
          isOpen={isActionsModalOpen}
          title={`Review ${activeSubmission?.contact.firstName} ${activeSubmission?.contact.lastName}`}
          onClose={onCloseActionsModal}
          submission={activeSubmission}
          fetchSubmissionsData={fetchSubmissionsData}
        />
      </div>
    </>
  );
};

export default AllSubmissionsDataGrid;
